/**
 * Pure validators of the two writes a tenant may perform on its parameter
 * tables: add a new code, or complete blank columns of an existing row.
 * They return French messages; the API service throws them unchanged so the
 * rules live in one place.
 */
import { isBlank, tableRows } from "./dgi-tables.js";
import { PARAMETER_ROW_FIELD_LABELS, PARAMETER_ROW_FIELDS, PARAMETER_TABLE_COLUMNS, PARAMETER_TABLE_LABELS, } from "./types.js";
/** Codes: uppercase letters and digits, one to eight characters. */
export const PARAMETER_CODE_PATTERN = /^[A-Z0-9]{1,8}$/;
/** Longest accepted invoice mention (free text). */
export const INVOICE_MENTION_MAX_LENGTH = 60;
/** Longest accepted label. */
export const PARAMETER_LABEL_MAX_LENGTH = 120;
/** Longest accepted description or « applicable dans le cas ». */
export const PARAMETER_TEXT_MAX_LENGTH = 500;
export const APPEND_ONLY_MESSAGE_PREFIX = "Les tableaux de paramétrage DGI sont en ajout seul";
/** Attributes a new row must carry, per table (booleans). */
const REQUIRED_BOOLEAN_ATTRIBUTES = {
    CLIENT_TYPE: ["requiresName", "requiresIFU", "acceptsNIF"],
    ARTICLE_TYPE: ["isService", "isImported"],
    CREDIT_NOTE_TYPE: [],
    COMMENT_LINE: [],
};
/** Attributes a new row may carry, per table (booleans). */
const OPTIONAL_BOOLEAN_ATTRIBUTES = {
    CLIENT_TYPE: [],
    ARTICLE_TYPE: [],
    CREDIT_NOTE_TYPE: ["requiresOriginalInvoice"],
    COMMENT_LINE: [],
};
const TABLES_WITH_MENTION = [
    "CLIENT_TYPE",
    "ARTICLE_TYPE",
    "CREDIT_NOTE_TYPE",
];
const ATTRIBUTE_SUBJECT = {
    CLIENT_TYPE: "un type de client",
    ARTICLE_TYPE: "un type d'article",
    CREDIT_NOTE_TYPE: "une nature d'avoir",
    COMMENT_LINE: "une ligne de commentaire",
};
function result(errors) {
    return { valid: errors.length === 0, errors };
}
function textLengthErrors(field, value) {
    if (isBlank(value))
        return [];
    const max = field === "invoiceMention"
        ? INVOICE_MENTION_MAX_LENGTH
        : field === "label"
            ? PARAMETER_LABEL_MAX_LENGTH
            : PARAMETER_TEXT_MAX_LENGTH;
    if (value.trim().length > max) {
        const name = PARAMETER_ROW_FIELD_LABELS[field];
        return [`La ${name} ne peut pas dépasser ${max} caractères.`];
    }
    return [];
}
/**
 * Checks a new row against the resolved tables of the tenant.
 * Comment lines refuse every addition: the annex fixes A–H.
 */
export function checkAddRow(tables, table, input) {
    const errors = [];
    const tableLabel = PARAMETER_TABLE_LABELS[table];
    if (table === "COMMENT_LINE") {
        return result([
            "Les lignes de commentaires sont fixées par l'annexe DGI (A à H) : aucune ligne ne peut être ajoutée, seules les lignes réservées C à H peuvent être complétées.",
        ]);
    }
    const code = input.code ?? "";
    if (!PARAMETER_CODE_PATTERN.test(code)) {
        errors.push(`Le code « ${code} » est invalide : 1 à 8 caractères, lettres majuscules (A-Z) ou chiffres (0-9).`);
    }
    else if (tableRows(tables, table).some((row) => row.code === code)) {
        errors.push(`Le code « ${code} » existe déjà dans la table « ${tableLabel} ».`);
    }
    if (isBlank(input.label)) {
        errors.push("Le libellé est requis.");
    }
    errors.push(...textLengthErrors("label", input.label));
    if (TABLES_WITH_MENTION.includes(table)) {
        if (isBlank(input.invoiceMention)) {
            errors.push("La mention sur la facture est requise.");
        }
        errors.push(...textLengthErrors("invoiceMention", input.invoiceMention));
    }
    errors.push(...textLengthErrors("description", input.description));
    errors.push(...textLengthErrors("applicableCase", input.applicableCase));
    const attributes = input.attributes !== null && typeof input.attributes === "object" ? input.attributes : {};
    for (const name of REQUIRED_BOOLEAN_ATTRIBUTES[table]) {
        if (typeof attributes[name] !== "boolean") {
            errors.push(`L'attribut « ${name} » (vrai/faux) est requis pour ${ATTRIBUTE_SUBJECT[table]}.`);
        }
    }
    for (const name of OPTIONAL_BOOLEAN_ATTRIBUTES[table]) {
        if (attributes[name] !== undefined && typeof attributes[name] !== "boolean") {
            errors.push(`L'attribut « ${name} » doit être vrai ou faux.`);
        }
    }
    const allowed = [...REQUIRED_BOOLEAN_ATTRIBUTES[table], ...OPTIONAL_BOOLEAN_ATTRIBUTES[table]];
    for (const name of Object.keys(attributes)) {
        if (!allowed.includes(name)) {
            errors.push(`L'attribut « ${name} » n'existe pas pour ${ATTRIBUTE_SUBJECT[table]}.`);
        }
    }
    return result(errors);
}
/**
 * Checks a completion patch against the resolved row: only blank columns of
 * the table may receive a value, and a value is never blanked.
 */
export function checkCompleteRow(tables, table, code, patch) {
    const errors = [];
    const tableLabel = PARAMETER_TABLE_LABELS[table];
    const row = tableRows(tables, table).find((candidate) => candidate.code === code);
    if (!row) {
        return result([`Le code « ${code} » n'existe pas dans la table « ${tableLabel} ».`]);
    }
    const fields = Object.keys(patch).filter((key) => patch[key] !== undefined);
    if (fields.length === 0) {
        return result(["Aucun champ à compléter."]);
    }
    for (const field of fields) {
        if (!PARAMETER_ROW_FIELDS.includes(field)) {
            errors.push(`Le champ « ${field} » n'existe pas.`);
            continue;
        }
        const name = PARAMETER_ROW_FIELD_LABELS[field];
        if (!PARAMETER_TABLE_COLUMNS[table].includes(field)) {
            errors.push(`Le champ « ${name} » n'existe pas dans la table « ${tableLabel} ».`);
            continue;
        }
        const value = patch[field];
        if (isBlank(value)) {
            errors.push(`La valeur de « ${name} » est vide : une valeur ne peut être ajoutée que si elle est renseignée.`);
            continue;
        }
        const current = row[field];
        if (!isBlank(current)) {
            errors.push(`${APPEND_ONLY_MESSAGE_PREFIX} : la valeur « ${current} » de « ${name} » ne peut pas être modifiée.`);
            continue;
        }
        errors.push(...textLengthErrors(field, value));
    }
    return result(errors);
}
//# sourceMappingURL=checks.js.map